import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { getIsAnyCollectiveUserPools, getIsRROrCRRWithoutCollectivePools, Location } from 'components/event_type_edit';
import { nonWebConferencingKinds } from 'components/event_type_edit/location/location_field_with_buttons/constants';
import { useEventTypeContext } from 'features/event_type_provider';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { AllHostsList, AllHostsLocationSelect } from './hosts';
import { anyMemberInUserPool, transformLocationConfigurationForInitialValues } from './utils';
import { useEnabledHosts } from '../../../hooks';
const headerWrapperCss = "h1dsq449";
const HeaderWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerWrapperCss, props.className)} />;
});
export const LocationField = ({
  isReadOnly,
  isLocked,
  showNoLocationWarningOnReopen = false
}) => {
  const {
    eventType,
    justCreated
  } = useEventTypeContext();
  const {
    isGroup,
    isCollective,
    isRoundRobin,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const {
    isMETParent,
    isUser
  } = eventType;
  const possibleNonWebConferencingKinds = isGroup ? nonWebConferencingKinds.filter(kind => kind !== 'ask_invitee') : nonWebConferencingKinds;
  const isNewCollectiveOrRoundRobinEventType = !eventType?.id && (isCollective || isRoundRobin);
  const {
    values: {
      memberships,
      userPools,
      locationConfigurations = []
    },
    setFieldValue,
    setFieldTouched
  } = useFormContext();
  const enabledHosts = useEnabledHosts();
  const isAnyCollectiveUserPools = getIsAnyCollectiveUserPools(userPools);
  const useRoundRobinLayout = getIsRROrCRRWithoutCollectivePools(isRoundRobin, isCollectiveRoundRobin, userPools);
  const handleChange = useCallback(value => {
    const updatedMemberships = memberships.map(membership => {
      if (isCollectiveRoundRobin && membership.user_pool_uid !== (userPools[0]?.uuid || userPools[0]?.key)) {
        return membership;
      }
      return {
        ...membership,
        locationConfiguration: transformLocationConfigurationForInitialValues(value)
      };
    });
    setFieldValue('memberships', updatedMemberships);
    setFieldTouched('memberships');
  }, [isCollectiveRoundRobin, memberships, userPools, setFieldValue, setFieldTouched]);
  if (isNewCollectiveOrRoundRobinEventType && enabledHosts.length === 0) {
    return null;
  }
  if (isCollectiveRoundRobin && !isAnyCollectiveUserPools && anyMemberInUserPool(memberships, userPools[0])) {
    return null;
  }
  if (useRoundRobinLayout) {
    return <>
        <HeaderWrapper>
          <BodyText>
            {I18n.t('publisher.event_type_editor.sections.event_details.location.round_robin_description')}
          </BodyText>
          <BodyText color="muted" size="medium">
            {I18n.t('publisher.event_type_editor.sections.event_details.location.round_robin_sub_description')}
          </BodyText>
        </HeaderWrapper>
        {!isReadOnly && <AllHostsLocationSelect onSelect={handleChange} eventType={eventType} />}
        <AllHostsList isReadOnly={isReadOnly} eventType={eventType} memberships={memberships} isManaged={isMETParent} isCollectiveRoundRobin={isCollectiveRoundRobin} userPools={userPools} />
      </>;
  }
  const showNoLocationWarning = (justCreated || showNoLocationWarningOnReopen) && isUser && !locationConfigurations.filter(l => !l._destroy).length;
  return <>
      <Location eventType={eventType} membership={memberships?.[0]} nonWebConferencingKinds={possibleNonWebConferencingKinds} isManaged={isMETParent} isReadOnly={isReadOnly} isLocked={isLocked} startExpanded />
      {showNoLocationWarning ? <InlineBanner variant="warning" message={I18n.t('publisher.event_type_editor.sections.event_details.location.no_location_warning')} isDismissible={false} /> : null}
    </>;
};

require("./location_field.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./location_field.js");